import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { alpha } from '@mui/material/styles';
import { Box, Button, Card, CardContent, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import React from 'react';

import coinCollectionHeroDesktop from '../assets/images/coin-collection-hero-desktop.png';
import coinCollectionHeroMobile from '../assets/images/coin-collection-hero-mobile.png';
import { trackEvent } from '../components/GoogleAnalytics';
import SEO from '../components/SEO';
import type { CoinTier } from '../types/coin';
import { coinTiers } from '../utils/coinTiers';
import { generateWebPageSchema } from '../utils/schemaGenerators';

const pageTitle = 'Commemorative Challenge Coins for Family Court Reform';
const pageDescription =
  'Support family court reform, civil rights, and advocacy outreach with a commemorative challenge coin recognizing your commitment to lasting change today.';

const CoinCard: React.FC<{ tier: CoinTier }> = ({ tier }) => {
  const checkoutEnabled = Boolean(tier.stripeUrl);

  const handleCheckout = () => {
    if (!tier.stripeUrl) return;

    trackEvent('coin_checkout_click', 'engagement', tier.id);
    window.open(tier.stripeUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card
      component="article"
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, tier.finish === 'premium' ? 0.75 : 0.35),
        background:
          tier.finish === 'premium'
            ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.16)}, ${theme.palette.background.paper} 45%)`
            : theme.palette.background.paper,
        boxShadow: tier.finish === 'premium' ? `0 12px 35px ${alpha(theme.palette.primary.main, 0.18)}` : 3,
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 1,
          p: 1.5,
          bgcolor: theme.palette.mode === 'dark' ? '#050505' : 'grey.100',
        })}
      >
        <Box
          component="img"
          src={tier.frontImage}
          alt={`${tier.name} challenge coin front`}
          loading="lazy"
          sx={{ display: 'block', width: '100%', borderRadius: 1 }}
        />
        <Box
          component="img"
          src={tier.backImage}
          alt={`${tier.name} challenge coin back`}
          loading="lazy"
          sx={{ display: 'block', width: '100%', borderRadius: 1 }}
        />
      </Box>

      <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Typography component="h3" variant="h5" sx={{ fontWeight: 800 }}>
            {tier.name}
          </Typography>
          <Chip
            size="small"
            icon={tier.finish === 'premium' ? <AutoAwesomeIcon /> : undefined}
            label={tier.finish === 'premium' ? 'Premium Finish' : 'Antique Finish'}
            color={tier.finish === 'premium' ? 'primary' : 'default'}
          />
        </Stack>
        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 800, mb: 1 }}>
          {tier.contributionRange}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          Receive this commemorative challenge coin in recognition of your commitment to advancing the movement.
        </Typography>
        <Button
          variant="contained"
          size="large"
          disabled={!checkoutEnabled}
          onClick={handleCheckout}
          endIcon={checkoutEnabled ? <ArrowForwardIcon /> : undefined}
          aria-label={
            checkoutEnabled
              ? `Contribute and receive the ${tier.name} challenge coin`
              : `${tier.name} challenge coin checkout coming soon`
          }
          sx={{ mt: 'auto' }}
        >
          {checkoutEnabled ? `Choose ${tier.name}` : 'Checkout Coming Soon'}
        </Button>
      </CardContent>
    </Card>
  );
};

const CoinSection: React.FC<{
  title: string;
  description: string;
  tiers: CoinTier[];
}> = ({ title, description, tiers }) => (
  <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
    <Box sx={{ maxWidth: 760, mx: 'auto', mb: 5, textAlign: 'center' }}>
      <Typography component="h2" variant="h2" sx={{ fontWeight: 800, mb: 2 }}>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.15rem' } }}>
        {description}
      </Typography>
    </Box>
    <Grid container spacing={3} justifyContent="center">
      {tiers.map((tier) => (
        <Grid item xs={12} md={tiers.length === 2 ? 6 : 4} key={tier.id}>
          <CoinCard tier={tier} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

const CommemorativeCoins: React.FC = () => {
  const antiqueTiers = coinTiers.filter((tier) => tier.finish === 'antique');
  const premiumTiers = coinTiers.filter((tier) => tier.finish === 'premium');

  const scrollToCoins = () => {
    document.getElementById('coin-collection')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box component="main">
      <SEO
        title={pageTitle}
        description={pageDescription}
        path="/commemorative-coins"
        image={`https://robertgarza.us${coinTiers[2].frontImage}`}
        schema={generateWebPageSchema({
          name: pageTitle,
          description: pageDescription,
          url: '/commemorative-coins',
        })}
      />

      <Box
        component="section"
        sx={(theme) => ({
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: { xs: 'flex-end', md: 'center' },
          minHeight: { xs: 680, md: 'clamp(560px, 46.875vw, 900px)' },
          px: 2,
          py: { xs: 6, md: 8 },
          color: 'common.white',
          backgroundColor: '#03060a',
          backgroundImage: {
            xs: `url(${coinCollectionHeroMobile})`,
            md: `url(${coinCollectionHeroDesktop})`,
          },
          backgroundPosition: { xs: 'center top', md: 'center' },
          backgroundRepeat: 'no-repeat',
          backgroundSize: { xs: '100% auto', md: 'cover' },
        })}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              maxWidth: { xs: 620, md: '48%' },
              mx: { xs: 'auto', md: 0 },
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Typography
              component="h1"
              variant="h1"
              sx={{ fontSize: { md: '2.75rem', lg: '3.25rem' }, fontWeight: 900, mb: 3, textShadow: '0 2px 18px #000' }}
            >
              Carry the Movement Forward
            </Typography>
            <Typography
              variant="h3"
              component="p"
              sx={{
                fontSize: { md: '1.35rem', lg: '1.5rem' },
                fontWeight: 400,
                lineHeight: 1.5,
                mb: 3,
                textShadow: '0 2px 12px #000',
              }}
            >
              Stand for justice. Strengthen families. Help turn advocacy into action.
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 4, fontSize: { xs: '1rem', md: '1.05rem' }, textShadow: '0 2px 10px #000' }}
            >
              Your contribution helps advance family court reform, civil and human rights education, community outreach,
              and accountable government. Choose a commemorative challenge coin that recognizes your commitment to
              lasting change.
            </Typography>
            <Button variant="contained" size="large" onClick={scrollToCoins} endIcon={<ArrowForwardIcon />}>
              Explore the Coin Collection
            </Button>
          </Box>
        </Container>
      </Box>

      <Container id="coin-collection" maxWidth="lg">
        <CoinSection
          title="Antique Finish Challenge Coins"
          description="Honor your place in the movement with a timeless finish and a symbol of shared purpose."
          tiers={antiqueTiers}
        />
        <CoinSection
          title="Premium Finish Challenge Coins"
          description="Recognize transformational support with a brilliant premium finish reserved for the movement's leading advocates."
          tiers={premiumTiers}
        />
      </Container>

      <Box
        component="section"
        sx={(theme) => ({
          py: { xs: 8, md: 11 },
          px: 2,
          textAlign: 'center',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
        })}
      >
        <Container maxWidth="md">
          <Typography component="h2" variant="h2" sx={{ fontWeight: 800, mb: 2 }}>
            Turn Your Support Into Lasting Impact
          </Typography>
          <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }}>
            Every contribution strengthens a community of advocates working to restore rights, rebuild families, and
            create meaningful reform. Carry the coin—and the mission—with pride.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default CommemorativeCoins;
